import { useMemo, useState } from "react"
import { FormControlLabel, IconButton, MenuItem, Popover, Radio, RadioGroup, Select, Stack, StackProps, Switch, Typography } from "@mui/material"
import { Settings as SettingsIcon } from '@mui/icons-material'
import { AuthorGraphCategories, AuthorGraphCategory, AuthorGraphType, AuthorGraphTypes, isAuthorGraphCategory, SymbolSizeRuleType, SymbolSizeRuleTypes } from "./types"
import { useUserSession } from "features/session/context"

type AuthorGraphOptionBarProps = {
	showSingleNode: boolean
	setShowSingleNode: (showSingleNode: boolean) => void
	graphType: AuthorGraphType
	setGraphType: (graphType: AuthorGraphType) => void
	category: AuthorGraphCategory
	setCategory: (category: AuthorGraphCategory) => void
	symbolSizeRule: SymbolSizeRuleType
	setSymbolSizeRule: (symbolSizeRule: SymbolSizeRuleType) => void
} & StackProps
export const AuthorGraphOptionBar = ({
	showSingleNode,
	setShowSingleNode,
	graphType,
	setGraphType,
	category,
	setCategory,
	symbolSizeRule,
	setSymbolSizeRule,
	...others
}: AuthorGraphOptionBarProps) => {
	const { isCareNet } = useUserSession()
	const [popup, setPopup] = useState<HTMLButtonElement | null>(null)
	const validCategories = useMemo(
		() => isCareNet === false ? (
			Object.entries(AuthorGraphCategories).filter(([category]) => (
				category !== 'kanriBunrui' &&
				category !== 'tierRank'
			))
		) : (
			Object.entries(AuthorGraphCategories)
		),
		[isCareNet]
	)
	return (
		<>
			<Stack {...others} spacing={1} direction='row'>
				<IconButton onClick={(ev) => setPopup(ev.currentTarget)}>
					<SettingsIcon/>
				</IconButton>

				<Select
					size="small"
					defaultValue={category}
					onChange={(event) => {
						const newValue = event.target.value
						if (isAuthorGraphCategory(newValue)) {
							setCategory(newValue as AuthorGraphCategory)
						}
					}}
				>
					{validCategories.map(([category, name]) => (
						<MenuItem key={category} value={category}>
							{name}
						</MenuItem>
					))}
				</Select>
			</Stack>

			{popup ? (
				<Popover
					anchorEl={popup}
					open={Boolean(popup)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					onClose={() => setPopup(null)}
				>
					<Stack direction="column" padding={2} spacing={1}>
						<Stack direction='row' alignItems='flex-start'>
							<Typography variant="body2" whiteSpace="nowrap" textAlign="right" width="5rem" paddingY={1}>形状</Typography>
							<RadioGroup
								value={graphType}
								onChange={(ev, value) => setGraphType(value as AuthorGraphType)}
								sx={{ paddingX: 1.5 }}
							>
								{Object.entries(AuthorGraphTypes).map(([key, label]) => (
									<FormControlLabel
										key={`graph-type-${key}`}
										value={key}
										control={<Radio size="small"/>}
										label={<Typography variant="body2" whiteSpace="nowrap">{label}</Typography>}
									/>
								))}
							</RadioGroup>
						</Stack>

						<Stack direction='row' alignItems='flex-start'>
							<Typography variant="body2" whiteSpace="nowrap" textAlign="right" width="5rem" paddingY={1}>大きさ基準</Typography>
							<RadioGroup
								value={symbolSizeRule}
								onChange={(ev, value) => setSymbolSizeRule(value as SymbolSizeRuleType)}
								sx={{ paddingX: 1.5 }}
							>
								{Object.entries(SymbolSizeRuleTypes).map(([key, label]) => (
									<FormControlLabel
										key={`symbol-size-${key}`}
										value={key}
										control={<Radio size="small"/>}
										label={<Typography variant="body2" whiteSpace="nowrap">{label}</Typography>}
									/>
								))}
							</RadioGroup>
						</Stack>

						<Stack direction='row' alignItems='center'>
							<Typography variant="body2" whiteSpace="nowrap" textAlign="right" width="5rem">単独著者</Typography>
							<Switch
								color="primary"
								value={showSingleNode}
								onChange={(ev, checked) => setShowSingleNode(checked)}
							/>
						</Stack>
					</Stack>					
				</Popover>
			) : <></>}
		</>
	)
}
